import * as React from 'react'
import { Alert, Box, Chip, Grid, LinearProgress, Paper, Stack, Typography } from '@mui/material'

const wallLabels = { left: 'Left', middle: 'Middle', right: 'Right' }
const formatTime = seconds => {
    const value = Number(seconds)
    if (!Number.isFinite(value) || value <= 0) return 'ready'
    const minutes = Math.floor(value / 60)
    return `${minutes}:${String(Math.floor(value % 60)).padStart(2, '0')}`
}

function Metric({ label, value }) {
    return <Paper variant="outlined" sx={{ p: 1.2, height: '100%', borderColor: 'rgba(129,146,178,.22)', background: 'rgba(14,25,44,.35)' }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography sx={{ mt: .25, fontWeight: 850, fontSize: '1.05rem' }}>{value ?? 'â€”'}</Typography>
    </Paper>
}

export default function RiftLiveDashboard({ status }) {
    if (!status?.active) return <Alert severity="info">{status?.message || (status ? 'No active RIFT portal was reported by the game.' : 'The live rift portal status appears here while the bot is running.')}</Alert>
    const health = Math.max(0, Math.min(100, Number(status.health ?? 0)))
    const shop = status.shop ?? {}
    const decisions = Array.isArray(status.decisions) ? status.decisions : []
    return <Stack spacing={1.5}>
        <Grid container spacing={1.2}>
            <Grid item xs={6} md={3}><Metric label="Boss level" value={status.bossLevelID} /></Grid>
            <Grid item xs={6} md={3}><Metric label="Boss phase" value={status.bossStageID} /></Grid>
            <Grid item xs={6} md={3}><Metric label="Boss eligibility points" value={`${status.playerPoints == null ? 'Waiting for game data' : Number(status.playerPoints).toLocaleString()}${status.bossPointTarget ? ` / ${Number(status.bossPointTarget).toLocaleString()}` : ''}`} /></Grid>
            <Grid item xs={6} md={3}><Metric label="Attacks" value={`${status.sentCount ?? 0} sent Â· ${status.cancelledCount ?? 0} recalled`} /></Grid>
        </Grid>
        <Metric label="Activity points" value={`${status.activityPoints == null ? 'Waiting for game data' : Number(status.activityPoints).toLocaleString()}${status.activityPointTarget ? ` / ${Number(status.activityPointTarget).toLocaleString()}` : ''}`} />
        <Paper variant="outlined" sx={{ p: 1.4, borderColor: 'rgba(129,146,178,.22)', background: 'rgba(14,25,44,.35)' }}>
            <Stack direction="row" justifyContent="space-between"><Typography fontWeight={850}>Portal health</Typography><Typography>{health}%</Typography></Stack>
            <LinearProgress variant="determinate" value={health} sx={{ mt: 1, height: 8, borderRadius: 4 }} />
        </Paper>
        <Grid container spacing={1.2}>
            {Object.entries(wallLabels).map(([key, label]) => {
                const wall = status.walls?.[key] ?? {}
                const standing = Number(wall.percentLeft ?? 0) > 0
                return <Grid item xs={12} md={4} key={key}><Paper variant="outlined" sx={{ p: 1.3, borderColor: 'rgba(129,146,178,.22)', background: 'rgba(14,25,44,.35)' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center"><Typography fontWeight={850}>{label} wall</Typography>
                        <Chip size="small" color={standing ? 'success' : 'default'} label={standing ? `${wall.percentLeft}% standing` : `down Â· ${formatTime(wall.regenerationSeconds)}`} /></Stack>
                </Paper></Grid>
            })}
        </Grid>
        <Grid container spacing={1.2}>
            <Grid item xs={12} md={6}><Metric label="RIFT shop" value={shop.checkedAt ? `${shop.boughtCount ?? 0} bought Â· ${Number(shop.spent ?? 0).toLocaleString()} spent Â· ${shop.state ?? 'ready'}` : 'Waiting for game data'} /></Grid>
        </Grid>
        {Array.isArray(status.runtimePlan) && status.runtimePlan.length ? <Box>
            <Typography fontWeight={850} sx={{ mb: .8 }}>Commander plan</Typography>
            <Stack spacing={.7}>{status.runtimePlan.map((item, index) => <Paper key={`${item.commanderPosition}-${index}`} variant="outlined" sx={{ px: 1.2, py: .8, borderColor: 'rgba(129,146,178,.18)' }}>
                <Stack direction="row" justifyContent="space-between"><Typography>Commander {item.commanderPosition}</Typography><Chip size="small" label={item.state ?? 'waiting'} /></Stack>
            </Paper>)}</Stack>
        </Box> : null}
        {decisions.length ? <Box><Typography fontWeight={850} sx={{ mb: .8 }}>Recent decisions</Typography>
            <Stack spacing={.55}>{decisions.slice(0, 10).map((item, index) => <Typography key={item.id ?? index} variant="body2" color="text.secondary">
                {item.action ?? 'info'} Â· {item.message ?? item.reason ?? 'RIFT decision'}
            </Typography>)}</Stack></Box> : null}
    </Stack>
}
